// Génère un PDF de projet aux couleurs Mopper (jsPDF, vectoriel, sans dépendance réseau).
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { getVolumeTotal, getVolumeByRoom, getInventoryCounts, getBoxTotal } from "@/lib/projectStore";
import { MOPPER_LOGO_URL } from "@/lib/seo";

async function makeQr(text) {
  try {
    return await QRCode.toDataURL(text, { margin: 1, width: 240, color: { dark: "#0A0F2C", light: "#FFFFFF" } });
  } catch { return null; }
}

const NAVY = [10, 15, 44];
const BLUE = [45, 91, 255];
const YELLOW = [255, 216, 77];
const GRAY = [107, 114, 128];
const DARK = [17, 24, 39];
const LIGHT = [247, 247, 248];
const LINE = [229, 231, 235];

function loadLogo(url) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const c = document.createElement("canvas");
          c.width = img.naturalWidth; c.height = img.naturalHeight;
          c.getContext("2d").drawImage(img, 0, 0);
          resolve({ dataUrl: c.toDataURL("image/png"), ar: img.naturalWidth / img.naturalHeight });
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    } catch { resolve(null); }
  });
}

export async function generateProjectPdf(project, T, lang = "fr") {
  const P = T.pdf;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, M = 16;
  const CW = W - M * 2;
  let y = 0;

  const setColor = (c) => doc.setTextColor(c[0], c[1], c[2]);
  const setFill = (c) => doc.setFillColor(c[0], c[1], c[2]);
  const setDraw = (c) => doc.setDrawColor(c[0], c[1], c[2]);
  const font = (style = "normal") => doc.setFont("helvetica", style);

  const dateStr = new Date().toLocaleDateString(lang === "de" ? "de-CH" : lang === "en" ? "en-GB" : "fr-CH", { day: "2-digit", month: "long", year: "numeric" });

  // ---- HEADER (bandeau blanc, vrai logo Mopper bleu) — répété sur chaque page ----
  const logo = await loadLogo(MOPPER_LOGO_URL);
  const header = () => {
    if (logo) {
      const h = 9; const w = Math.min(48, h * logo.ar);
      doc.addImage(logo.dataUrl, "PNG", M, 14, w, h);
    } else {
      font("bold"); doc.setFontSize(24); setColor(BLUE);
      doc.text("mopper", M, 22);
      const wm = doc.getTextWidth("mopper"); doc.text(".", M + wm + 0.5, 22);
    }
    // Filet bleu sous le logo
    setDraw(BLUE); doc.setLineWidth(0.8); doc.line(M, 27, M + 22, 27);
    // Titre projet + date
    font("bold"); doc.setFontSize(20); setColor(NAVY);
    doc.text(P.title, M, 40);
    font("normal"); doc.setFontSize(9.5); setColor(GRAY);
    doc.text(`${P.generatedOn} ${dateStr}`, M, 46);
    y = 60;
  };
  header();

  // ---- SUMMARY CARDS ----
  const vol = getVolumeTotal(project);
  const boxes = getBoxTotal(project);
  const inv = getInventoryCounts(project);
  const cards = [
    { label: T.summary.volume, value: vol > 0 ? `${vol} m³` : "—" },
    { label: T.summary.boxes, value: boxes > 0 ? String(boxes) : "—" },
    { label: T.summary.inventory, value: inv.items > 0 ? `${inv.items}` : "—", sub: inv.items > 0 ? `${inv.rooms} ${T.page.project.rooms}` : "" },
  ];
  const gap = 5; const cw = (CW - gap * 2) / 3; const ch = 26;
  cards.forEach((c, i) => {
    const x = M + i * (cw + gap);
    setFill(LIGHT); doc.roundedRect(x, y, cw, ch, 2.5, 2.5, "F");
    setColor(GRAY); font("bold"); doc.setFontSize(7.5);
    doc.text(c.label.toUpperCase(), x + 6, y + 8);
    setColor(BLUE); font("bold"); doc.setFontSize(18);
    doc.text(String(c.value), x + 6, y + 19);
    if (c.sub) { setColor(GRAY); font("normal"); doc.setFontSize(8); doc.text(c.sub, x + 6, y + 24); }
  });
  y += ch + 12;

  // ---- helper: section title ----
  const sectionTitle = (txt) => {
    ensure(16);
    setFill(BLUE); doc.rect(M, y - 3.5, 3, 5, "F");
    setColor(NAVY); font("bold"); doc.setFontSize(12.5);
    doc.text(txt, M + 6, y);
    y += 8;
  };
  const ensure = (need) => {
    if (y + need > 280) { footer(); doc.addPage(); header(); }
  };
  const row = (left, right, opts = {}) => {
    ensure(8);
    font(opts.bold ? "bold" : "normal"); doc.setFontSize(opts.size || 10);
    setColor(opts.color || DARK);
    doc.text(String(left), M + (opts.indent || 0), y);
    if (right != null) { const rt = String(right); const rw = doc.getTextWidth(rt); doc.text(rt, W - M - rw, y); }
    if (opts.divider !== false) { setDraw(LINE); doc.setLineWidth(0.15); doc.line(M, y + 2, W - M, y + 2); }
    y += opts.gap || 7;
  };

  // ---- VOLUME PER ROOM ----
  const byRoom = getVolumeByRoom(project);
  const roomsVol = Object.entries(byRoom).filter(([, v]) => v > 0);
  if (vol > 0) {
    sectionTitle(P.volumeSection);
    roomsVol.forEach(([rk, v]) => row(T.rooms[rk] || rk, `${v} m³`));
    row(P.totalVolume, `${vol} m³`, { bold: true, color: BLUE, size: 11, gap: 9 });
  }

  // ---- BOXES ----
  const boxRes = project?.boxes?.result;
  if (boxRes && boxes > 0) {
    sectionTitle(P.boxesSection);
    Object.entries(boxRes.breakdown || {}).forEach(([k, v]) => row(T.boxes.types[k] || k, v));
    row(P.boxesTotal, `${boxRes.min}–${boxRes.max} ${T.boxes.boxesUnit}`, { bold: true, color: BLUE, size: 11, gap: 9 });
  }

  // ---- INVENTORY PER ROOM ----
  const inventory = project?.inventory;
  if (inventory && (inventory.items || []).length > 0) {
    sectionTitle(P.invSection);
    for (const r of inventory.rooms) {
      const direct = inventory.items.filter((it) => it.roomId === r.id && !it.containerId);
      const conts = inventory.containers.filter((c) => c.roomId === r.id);
      const count = inventory.items.filter((it) => it.roomId === r.id).reduce((s, it) => s + (Number(it.qty) || 1), 0);
      if (direct.length === 0 && conts.length === 0) continue;
      ensure(10);
      setFill([234, 240, 255]); doc.roundedRect(M, y - 4, CW, 7, 1.5, 1.5, "F");
      setColor(NAVY); font("bold"); doc.setFontSize(10.5);
      doc.text(r.name, M + 3, y);
      const ct = `${count} ${T.page.project.elements}`; const ctw = doc.getTextWidth(ct);
      setColor(GRAY); font("normal"); doc.setFontSize(9); doc.text(ct, W - M - 3 - ctw, y);
      y += 8;
      const printItem = (it, indent) => {
        const tags = [it.fragile && T.inventory.fragile, it.heavy && T.inventory.heavy, it.special && T.inventory.special].filter(Boolean).join(", ");
        const act = it.action && it.action !== "demenager" ? ` · ${T.actions[it.action]}` : "";
        const left = `${it.name}  ×${it.qty}${tags ? `  (${tags})` : ""}${act}`;
        row(left, null, { indent, size: 9.5, color: DARK });
      };
      direct.forEach((it) => printItem(it, 4));
      conts.forEach((c) => {
        row(`▸ ${c.name}`, null, { indent: 4, bold: true, size: 9.5, color: BLUE, divider: false, gap: 6 });
        const items = inventory.items.filter((it) => it.containerId === c.id);
        if (items.length === 0) row(T.inventory.emptyContainer, null, { indent: 9, size: 9, color: GRAY });
        else items.forEach((it) => printItem(it, 9));
      });
      y += 2;
    }
  }

  // ---- DISCLAIMER + FOOTER ----
  ensure(24);
  y += 4;
  setFill(LIGHT); doc.roundedRect(M, y, CW, 16, 2, 2, "F");
  setColor(GRAY); font("normal"); doc.setFontSize(8);
  doc.text(doc.splitTextToSize(P.disclaimer, CW - 8), M + 4, y + 6);
  y += 16 + 10;

  // ---- CONVERSION QR CODES (3 côte à côte, une seule ligne) ----
  const qrTargets = [
    { url: "https://gomopper.ch/devis", label: P.ctaBook },
    { url: "https://wa.me/41782321850", label: P.ctaWhatsapp },
    { url: "mailto:hello@gomopper.ch", label: P.ctaEmail },
  ];
  const qrImgs = await Promise.all(qrTargets.map((t) => makeQr(t.url)));
  const qrSize = 30;
  const qrTitleH = 10, qrLabelH = 8;
  ensure(qrTitleH + qrSize + qrLabelH + 4);
  // Titre du bloc
  setColor(NAVY); font("bold"); doc.setFontSize(12);
  const ctaLines = doc.splitTextToSize(P.ctaTitle, CW);
  doc.text(ctaLines, W / 2, y, { align: "center" });
  y += ctaLines.length * 5.5 + 4;
  const qrGap = (CW - qrSize * 3) / 2;
  qrTargets.forEach((t, i) => {
    const x = M + i * (qrSize + qrGap);
    if (qrImgs[i]) doc.addImage(qrImgs[i], "PNG", x, y, qrSize, qrSize);
    setColor(BLUE); font("bold"); doc.setFontSize(9.5);
    doc.text(t.label, x + qrSize / 2, y + qrSize + 5, { align: "center" });
  });
  y += qrSize + qrLabelH;

  function footer() {
    const fy = 289;
    setDraw(LINE); doc.setLineWidth(0.2); doc.line(M, fy - 4, W - M, fy - 4);
    setColor(GRAY); font("normal"); doc.setFontSize(7.5);
    doc.text(P.footer, M, fy);
    const pg = `${P.page} ${doc.internal.getNumberOfPages()}`;
    const pw = doc.getTextWidth(pg); doc.text(pg, W - M - pw, fy);
  }
  footer();

  doc.save(`mopper-projet-${new Date().toISOString().slice(0, 10)}.pdf`);
}
